import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, CardHeader, Table, Badge, Progress, Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { DatosSesion } from "../../Components/Common/DatosSesion";
import { getRendimientoMotorizados } from "../../helpers/backend_helper";
import WidgetLoader from "./WidgetLoader";

const RendimientoMotorizados = React.memo(({ fechaInicio, fechaFin, codigoProveedor, aseguradora }) => {
  const [motorizados, setMotorizados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [modalAyuda, setModalAyuda] = useState(false);
  const primeraVezRef = useRef(true);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Validar que tengamos fechas
    if (!fechaInicio || !fechaFin) {
      console.warn("RendimientoMotorizados - Faltan fechas:", { fechaInicio, fechaFin });
      return;
    }
    
    // Si es la primera vez y es proveedor sin código, esperar
    if (primeraVezRef.current) {
      const perfil = DatosSesion("perfil") || "";
      const esAdministrador = perfil === "Administrador" || perfil === "MyHealth";
      
      if (!esAdministrador && codigoProveedor === null) {
        //console.log("⏳ RendimientoMotorizados - Esperando código de proveedor...");
        return;
      }
      
      primeraVezRef.current = false;
    }
    
    // Cancelar timeout anterior si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce: esperar 100ms antes de ejecutar
    timeoutRef.current = setTimeout(() => {
      //console.log("📊 RendimientoMotorizados - Cargando con:", { fechaInicio, fechaFin, codigoProveedor });
      cargarMotorizados();
    }, 100);
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fechaInicio, fechaFin, codigoProveedor, aseguradora]);

  const cargarMotorizados = async () => {
    setCargando(true);
    setError(null);

    try {
      const params = {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        top: 5
      };

      if (codigoProveedor && codigoProveedor.trim() !== "") {
        params.codigo_proveedor = codigoProveedor;
      }

      if (aseguradora) {
        params.aseguradora = aseguradora;
      }

      const response = await getRendimientoMotorizados(params);
      //console.log("🚗 Response completa:", response);
      //console.log("🚗 response.data:", response.data);
      //console.log("🚗 response.data.data:", response.data?.data);
      // APIClient devuelve { data: { data: [...], status_code, message } }
      const datos = response.data?.data || [];
      //console.log("🚗 Datos a setear:", datos, "Length:", datos.length);
      setMotorizados(datos);
    } catch (err) {
      console.error("Error al cargar motorizados:", err);
      setError(err.message || "Error al cargar motorizados");
    } finally {
      setCargando(false);
    }
  };

  const formatearNumero = (numero) => {
    return numero.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getColorEficiencia = (porcentaje) => {
    if (porcentaje >= 90) return "success"; // Verde
    if (porcentaje >= 70) return "warning"; // Amarillo
    return "danger"; // Rojo
  };

  return (
    <Card className="card-height-100">
      <CardHeader className="align-items-center d-flex">
        <h4 className="card-title mb-0 flex-grow-1">
          <i className="ri-e-bike-2-line me-2 text-primary"></i>
          Rendimiento de Motorizados (Top 5)
        </h4>
        <div className="flex-shrink-0">
          <Button
            color="light"
            size="sm"
            className="btn-icon"
            onClick={() => setModalAyuda(true)}
            title="Ayuda sobre cálculos"
          >
            <i className="ri-question-line"></i>
          </Button>
        </div>
      </CardHeader>

      <CardBody>
        {cargando ? (
          <div className="table-responsive">
            <Table className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "25%" }}><span className="placeholder col-6"></span></th>
                  {[1, 2, 3, 4, 5].map(i => (
                    <th key={i} className="text-center" style={{ width: "15%" }}>
                      <div className="d-flex flex-column align-items-center placeholder-glow">
                        <span className="placeholder col-8 mb-1"></span>
                        <span className="placeholder col-4"></span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map(i => (
                  <tr key={i}>
                    <td>
                      <div className="d-flex align-items-center placeholder-glow">
                        <span className="avatar-xs rounded-circle placeholder me-2"></span>
                        <div className="flex-grow-1">
                           <span className="placeholder col-8 mb-1 d-block"></span>
                           <span className="placeholder col-5"></span>
                        </div>
                      </div>
                    </td>
                    {[1, 2, 3, 4, 5].map(j => (
                      <td key={j} className="text-center placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </div>
        ) : motorizados.length === 0 ? (
          <div className="text-center py-4">
            <i className="ri-inbox-line fs-1 text-muted"></i>
            <p className="text-muted mt-2">No hay datos de entregas para el período seleccionado</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="table-hover table-striped align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "25%" }}>Motorizado</th>
                    <th className="text-center" style={{ width: "15%" }}>
                      <div className="d-flex flex-column align-items-center">
                        <span>Entregas</span>
                        <small className="text-muted fw-normal">(cantidad)</small>
                      </div>
                    </th>
                    <th className="text-center" style={{ width: "15%" }}>
                      <div className="d-flex flex-column align-items-center">
                        <span>Distancia</span>
                        <small className="text-muted fw-normal">(km)</small>
                      </div>
                    </th>
                    <th className="text-center" style={{ width: "15%" }}>
                      <div className="d-flex flex-column align-items-center">
                        <span>Costo Total</span>
                        <small className="text-muted fw-normal">(USD)</small>
                      </div>
                    </th>
                    <th className="text-center" style={{ width: "15%" }}>
                      <div className="d-flex flex-column align-items-center">
                        <span>Costo/Km</span>
                        <small className="text-muted fw-normal">(USD)</small>
                      </div>
                    </th>
                    <th className="text-center" style={{ width: "15%" }}>
                      <div className="d-flex flex-column align-items-center">
                        <span>Eficiencia</span>
                        <small className="text-muted fw-normal">(% a tiempo)</small>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {motorizados.map((motorizado, index) => (
                    <tr key={index}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 me-2">
                            <div
                              className={`avatar-xs rounded-circle ${
                                index === 0
                                  ? "bg-success-subtle"
                                  : index === 1
                                  ? "bg-info-subtle"
                                  : index === 2
                                  ? "bg-warning-subtle"
                                  : "bg-secondary-subtle"
                              }`}
                            >
                              <span
                                className={`avatar-title rounded-circle fs-14 ${
                                  index === 0
                                    ? "text-success"
                                    : index === 1
                                    ? "text-info"
                                    : index === 2
                                    ? "text-warning"
                                    : "text-secondary"
                                }`}
                              >
                                {index + 1}
                              </span>
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-0 fw-medium">{motorizado.nombre_completo}</h6>
                            {motorizado.proveedores_str && (
                              <small className="text-muted">
                                <i className="ri-store-2-line me-1"></i>
                                {motorizado.proveedores_str}
                              </small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <Badge color="primary" className="fs-13">
                          {motorizado.cantidad_entregas}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <span className="fw-medium text-info">
                          {formatearNumero(motorizado.distancia_total)} km
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fw-medium text-success">
                          $ {formatearNumero(motorizado.costo_total_usd)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fw-medium">
                          $ {formatearNumero(motorizado.costo_por_km)}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <small className="text-muted">
                              {motorizado.entregas_a_tiempo}/{motorizado.cantidad_entregas}
                            </small>
                            <Badge color={getColorEficiencia(motorizado.porcentaje_a_tiempo)} className="fs-11">
                              {motorizado.porcentaje_a_tiempo}%
                            </Badge>
                          </div>
                          <Progress
                            value={motorizado.porcentaje_a_tiempo}
                            color={getColorEficiencia(motorizado.porcentaje_a_tiempo)}
                            className="progress-sm"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Leyenda */}
            <div className="mt-3 pt-3 border-top">
              <div className="row g-2">
                <div className="col-md-6">
                  <small className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    <strong>Eficiencia:</strong> Porcentaje de entregas realizadas a tiempo (mismo día o antes de la fecha pautada)
                  </small>
                </div>
                <div className="col-md-6">
                  <small className="text-muted">
                    <i className="ri-calculator-line me-1"></i>
                    <strong>Costo/Km:</strong> Costo promedio por kilómetro recorrido
                  </small>
                </div>
              </div>
            </div>
          </>
        )}
      </CardBody>

      {/* Modal de Ayuda */}
      <Modal isOpen={modalAyuda} toggle={() => setModalAyuda(!modalAyuda)} size="lg">
        <ModalHeader toggle={() => setModalAyuda(!modalAyuda)}>
          <i className="ri-information-line me-2 text-primary"></i>
          Cómo se Calcula el Rendimiento de Motorizados
        </ModalHeader>
        <ModalBody>
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-calculator-line me-2"></i>
              Descripción de Métricas
            </h5>
            
            <div className="table-responsive">
              <Table className="table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '25%' }}>Métrica</th>
                    <th style={{ width: '35%' }}>Cálculo</th>
                    <th style={{ width: '40%' }}>Interpretación</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Entregas Totales</strong></td>
                    <td>
                      Cantidad de entregas asignadas
                      <br />
                      <code className="text-muted">COUNT(deliveries)</code>
                    </td>
                    <td>Volumen de trabajo del motorizado</td>
                  </tr>
                  <tr>
                    <td><strong>Entregas a Tiempo</strong></td>
                    <td>
                      Entregas realizadas el mismo día o antes
                      <br />
                      <code className="text-muted">fecha_entrega ≤ fecha_pautada</code>
                    </td>
                    <td>Cumplimiento de plazos</td>
                  </tr>
                  <tr>
                    <td><strong>Entregas Tardías</strong></td>
                    <td>
                      Entregas realizadas después de la fecha pautada
                      <br />
                      <code className="text-muted">fecha_entrega &gt; fecha_pautada</code>
                    </td>
                    <td>Incumplimientos de plazos</td>
                  </tr>
                  <tr>
                    <td><strong>% Éxito</strong></td>
                    <td>
                      Porcentaje de entregas a tiempo
                      <br />
                      <code className="text-muted">(a_tiempo / total) × 100</code>
                    </td>
                    <td>
                      Eficiencia del motorizado:
                      <br />
                      <span className="text-success">• ≥90%: Excelente</span>
                      <br />
                      <span className="text-warning">• 70-89%: Bueno</span>
                      <br />
                      <span className="text-danger">• &lt;70%: Necesita mejora</span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Proveedores</strong></td>
                    <td>
                      Lista de farmacias atendidas
                      <br />
                      <code className="text-muted">UNIQUE(proveedores)</code>
                    </td>
                    <td>Diversificación de clientes</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-file-list-3-line me-2"></i>
              Ejemplo Práctico
            </h5>
            
            <div className="alert alert-info">
              <h6 className="alert-heading">
                <i className="ri-e-bike-2-line me-2"></i>
                Motorizado: José Pérez
              </h6>
              <hr />
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>Entregas Totales:</strong> 45
                    <br />
                    <small className="text-muted">→ Alto volumen de trabajo</small>
                  </p>
                  <p className="mb-2">
                    <strong>A Tiempo:</strong> 42
                    <br />
                    <small className="text-muted">→ Buen cumplimiento</small>
                  </p>
                  <p className="mb-2">
                    <strong>Tardías:</strong> 3
                    <br />
                    <small className="text-muted">→ Pocos retrasos</small>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>% Éxito:</strong> 93.33% <span className="badge bg-success">Excelente</span>
                    <br />
                    <small className="text-muted">→ Muy eficiente</small>
                  </p>
                  <p className="mb-2">
                    <strong>Proveedores:</strong> Farmaignacio, Caracas
                    <br />
                    <small className="text-muted">→ Atiende 2 farmacias</small>
                  </p>
                </div>
              </div>
              <hr />
              <p className="mb-0">
                <strong>Conclusión:</strong> José es un motorizado <strong className="text-success">altamente eficiente</strong> con 
                93% de entregas a tiempo. Solo 3 de 45 entregas llegaron tarde, lo que indica 
                <strong> excelente desempeño</strong> y confiabilidad.
              </p>
            </div>
          </div>

          <div className="mb-3">
            <h5 className="text-primary mb-3">
              <i className="ri-lightbulb-line me-2"></i>
              Recomendaciones de Análisis
            </h5>
            
            <ul className="list-unstyled">
              <li className="mb-2">
                <i className="ri-check-line text-success me-2"></i>
                <strong>% Éxito ≥90%:</strong> Motorizado confiable, considerar para entregas prioritarias
              </li>
              <li className="mb-2">
                <i className="ri-alert-line text-warning me-2"></i>
                <strong>% Éxito 70-89%:</strong> Buen desempeño, identificar causas de retrasos ocasionales
              </li>
              <li className="mb-2">
                <i className="ri-error-warning-line text-danger me-2"></i>
                <strong>% Éxito &lt;70%:</strong> Requiere atención, revisar rutas o capacitación
              </li>
              <li className="mb-2">
                <i className="ri-information-line text-info me-2"></i>
                <strong>Alto volumen + Alta eficiencia:</strong> Motorizado estrella del equipo
              </li>
              <li className="mb-2">
                <i className="ri-search-line text-primary me-2"></i>
                <strong>Múltiples proveedores:</strong> Motorizado versátil y experimentado
              </li>
            </ul>
          </div>

          <div className="alert alert-warning">
            <h6 className="alert-heading">
              <i className="ri-time-line me-2"></i>
              Nota sobre Fechas
            </h6>
            <p className="mb-0">
              Una entrega se considera <strong>"a tiempo"</strong> si se realiza el mismo día o antes de la fecha pautada. 
              Esto permite flexibilidad para entregas anticipadas sin penalizar al motorizado.
            </p>
          </div>

          <div className="text-center">
            <Button color="primary" onClick={() => setModalAyuda(false)}>
              <i className="ri-check-line me-1"></i>
              Entendido
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Card>
  );
});

export default RendimientoMotorizados;
